/* eslint-disable react/react-in-jsx-scope */
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useFeedback } from "@/dashboard/store/store";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import useImageCompressor from "@/hooks/useImageCompressor";
import { useMutateData } from "@/hooks/useMutation";
import usePostMultipart from "@/hooks/usePostMultipart";
import { FabricImage } from "fabric";
import { FabricJSCanvas, useFabricJSEditor } from "fabricjs-react";
import {
  Camera,
  Download,
  PencilLine,
  Plus,
  Square,
  Type,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileInput, FileUploader } from "./extension/file-upload";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { toast } from "./ui/use-toast";
import { error } from "console";

export default function ReportFeedback() {
  const [screenshot, setScreenshot] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [logError, setLogError] = useState("");
  const containerRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const [texte, setTexte] = useState("");
  const [type, setType] = useState("");
  const { t } = useTranslation("dashboard", {
    keyPrefix: "feedback",
  });
  const { handleCompressImage } = useImageCompressor();
  const { postMultipart } = usePostMultipart();
  const [uploadLogoLoading, setUploadLogoLoading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [idMedia, setIdMedia] = useState(null);
  const [view, setView] = useState(0); // 0 send feedback, 1 list report
  const [extensionInstalled, setExtensionInstalled] = useState(false);
  const { editor, onReady } = useFabricJSEditor();
  const feedbacks = useFetchDataV2(
    `${import.meta.env.VITE_API_ENDPOINT}/feedbacks`,
    "feedbacks",
    1000 * 60 * 60
  );

  // AJOUT : Récupération des données du store pour gérer les erreurs
  const {
    visibleFeedback,
    setVisibleFeedback,
    errorLog,
    clearErrorLog,
  } = useFeedback();

  // AJOUT : Pré-remplir le formulaire avec les données d'erreur si elles existent
  useEffect(() => {
    if (errorLog && visibleFeedback) {
      // setTitle(`Erreur: ${errorLog.name || 'Erreur inconnue'}`);
      setLogError(
        `Erreur: ${errorLog.name || 'Erreur inconnue'}`+`\n\n` +
        `Message: ${errorLog.message || ''}\n\n` +
        `Stack: ${errorLog.stack || ''}\n\n` +
        `Component Stack: ${errorLog.componentStack || ''}\n\n` +
        `URL: ${errorLog.url || ''}\n` +
        `User Agent: ${errorLog.userAgent || ''}\n` +
        `Time: ${errorLog.time || ''}`
      );
      setType("1"); // Type probleme
    }
  }, [errorLog, visibleFeedback]);

  const truncate = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + "…" : text;
  };

  useEffect(() => {
    const resizeCanvasAndBackground = () => {
      if (!editor?.canvas || !screenshot || !canvasContainerRef.current) {
        return;
      }

      const containerWidth = canvasContainerRef.current.clientWidth;
      if (containerWidth === 0) return;

      FabricImage.fromURL(screenshot, { crossOrigin: "anonymous" })
        .then((img) => {
          const canvas = editor.canvas;
          const scaleFactor = containerWidth / img.width;
          const scaledHeight = img.height * scaleFactor;

          canvas.setWidth(containerWidth);
          canvas.setHeight(scaledHeight);
          canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), {
            scaleX: scaleFactor,
            scaleY: scaleFactor,
            crossOrigin: "anonymous",
          });

          canvas.discardActiveObject();
          canvas.renderAll();
        })
        .catch((error) => {
          console.error(
            "resizeCanvasAndBackground: Error loading image:",
            error
          );
        });
    };

    const timer = setTimeout(resizeCanvasAndBackground, 100);
    window.addEventListener("resize", resizeCanvasAndBackground);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", resizeCanvasAndBackground);
    };
  }, [editor, screenshot]); // MODIFIÉ : Retiré isOpen car non utilisé

  const drawRect = () => {
    if (!editor) return;
    editor.addRectangle();
  };

  const addText = (text) => {
    if (!editor) return;
    editor.addText(text);
  };

  const clearCanvas = () => {
    if (!editor) return;
    editor.deleteAll();
  };

  const { mutate: addReportFeedback } = useMutateData({
    method: "POST",
    id: "feedbacks", // MODIFIÉ : Changé de "parcels" à "feedbacks"
    onSuccess: (data) => {
      console.log("✅ Feedback ajouté avec succès :", data);
      // AJOUT : Rafraîchir la liste des feedbacks
      if (feedbacks.refetch) {
        feedbacks.refetch();
      }
    },
  });

  const clientInfo = {
    userAgent: navigator.userAgent,
    screen: `${window.screen.width}x${window.screen.height}`,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let mediaId = idMedia;
    const url = window.location.href;

    if (extensionInstalled) {
      // Récupère l'image éditée du canvas
      const editedImage = editor?.canvas.toDataURL({
        format: "png",
        quality: 1,
      });

      // Convertir en blob pour upload
      const response = await fetch(editedImage);
      const blob = await response.blob();

      // Compresser l'image avant upload
      const compressedBlob = await handleCompressImage(blob, 800, 600, 0.8);

      // Préparer les données
      const data = new FormData();
      data.append("type", 13);
      data.append("files[]", compressedBlob || blob);

      setUploadLogoLoading(true);
      const newMedias = await postMultipart({
        url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
        data,
      });

      if (
        !Array.isArray(newMedias) &&
        newMedias.success !== undefined &&
        newMedias.success !== null &&
        !newMedias.success
      ) {
        toast({
          title: newMedias.message,
          duration: 1000,
        });
        setUploadLogoLoading(false);
        return;
      }

      mediaId = newMedias[0].id;
      setPhotoUrl(newMedias[0].uri);
      setIdMedia(newMedias[0].id);
      setUploadLogoLoading(false);
    }

    // Envoi du feedback
    addReportFeedback({
      endpoint: `${import.meta.env.VITE_API_ENDPOINT}/feedbacks`,
      body: {
        title,
        message,
        type: Number(type),
        pjs: mediaId ? [mediaId] : [],
        url,
        more: clientInfo, // MODIFIÉ : Utiliser errorLog si disponible
        errorLog: logError, // AJOUT : Ajouter le log d'erreur au corps de la requête
      },
    });

    toast({
      title: t("Merci pour votre signalement"),
      duration: 1000,
    });

    // Reset
    setScreenshot("");
    setTitle("");
    setMessage("");
    setType("");
    setPhotoUrl("");
    setIdMedia(null);
    setView(1);
    editor?.deleteAll();
    clearErrorLog(); // AJOUT : Nettoyer le log d'erreur
  };

  const enableExtension = false;

  const handlePictureChange = async (value) => {
    if (value) {
      setUploadLogoLoading(true);
      const data = new FormData();

      data.append("type", 13);

      // Compresser l'image avant l'upload
      const compressedBlob = await handleCompressImage(value[0], 500, 300, 0.9);
      console.log("compressedBlob", compressedBlob);
      if (compressedBlob) {
        for (let i = 0; i < value.length; i++) {
          data.append("files[]", compressedBlob);
        }
      } else {
        for (let i = 0; i < value.length; i++) {
          data.append("files[]", value[i]);
        }
      }
      const newMedias = await postMultipart({
        url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
        data,
      });
      if (
        !Array.isArray(newMedias) &&
        newMedias.success !== undefined &&
        newMedias.success !== null &&
        !newMedias.success
      ) {
        toast({
          title: newMedias.message,
          duration: 1000,
        });
        setUploadLogoLoading(false);
        return;
      }

      setPhotoUrl(newMedias[0].uri);

      setIdMedia(newMedias[0].id);
      setUploadLogoLoading(false);
      return;
    }
  };

  return (
    <div ref={containerRef} data-html2canvas-ignore>
      <Dialog open={visibleFeedback} onOpenChange={setVisibleFeedback}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-auto bg-transparent border-0 pt-10">
          <div className="flex items-center gap-0.5 absolute top-0 left-0 z-[9999999999999] pl-[24.8px]">
            <button
              className={`${
                view === 0 ? "bg-white" : "bg-white/50"
              } px-4 py-3 rounded-t-lg`}
              onClick={() => setView(0)}
            >
              {t("Signaler un problème")}
            </button>
            {!feedbacks.loading && feedbacks?.data?.length > 0 && (
              <button
                className={`${
                  view === 1 ? "bg-white" : "bg-white/50"
                } px-4 py-3 rounded-t-lg`}
                onClick={() => setView(1)}
                disabled={!feedbacks.loading && feedbacks?.data?.length === 0}
              >
                {t("Mes signalements")}
              </button>
            )}
          </div>
          {view === 0 ? (
            <form onSubmit={handleSubmit} className="flex gap-4 items-start">
              {extensionInstalled ? (
                <div className="w-2/3">
                  <div className="flex gap-3 bg-gray-900 py-4 rounded-t-lg px-4">
                    <button
                      type="button"
                      onClick={drawRect}
                      className="text-background size-8 rounded-sm hover:bg-gray-600 flex items-center justify-center"
                      title={t("Ajouter un rectangle")}
                    >
                      <Square size={20} />
                    </button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="text-background size-8 rounded-sm hover:bg-gray-600 flex items-center justify-center"
                          title={t("Ajouter un texte")}
                        >
                          <Type size={20} />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 ">
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Label className="block mb-2">{t("Texte")}</Label>
                            <Input
                              defaultValue={t("Texte")}
                              value={texte}
                              onChange={(e) => setTexte(e.target.value)}
                              className="col-span-2 h-8"
                            />
                          </div>
                          <button
                            type="button"
                            className="size-8 flex items-center justify-center bg-green-500"
                            onClick={() => addText(texte)}
                          >
                            <Plus />
                          </button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <button
                      type="button"
                      onClick={clearCanvas}
                      className="text-background h-8 px-2 rounded-sm hover:bg-gray-600 flex items-center justify-center gap-2"
                      title={t("Annuler les modifications")}
                    >
                      <X className="text-red-600" />{" "}
                      {t("Réinitialiser la modification")}
                    </button>
                  </div>
                  <div className="bg-gray-950 p-8">
                    <div ref={canvasContainerRef}>
                      <FabricJSCanvas
                        className="border rounded w-full min-h-[400px] bg-gray-50"
                        onReady={onReady}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-2/3 h-full">
                  <div className="bg-white h-full w-full rounded-md relative flex flex-col">
                    <h2 className="text-lg font-semibold text-gray-800 p-4">
                      {t("Ajouter une capture d'écran")}
                    </h2>
                    <div className="flex flex-1 pb-4 px-4">
                      <div className="w-full m-auto border border-dashed h-full ">
                        <FileUploader
                          value={[]}
                          onValueChange={handlePictureChange}
                          dropzoneOptions={{
                            multiple: false,
                          }}
                          className={`${photoUrl ? "" : "relative size-full"}`}
                        >
                          <FileInput className="size-full rounded-[20px] border border-dashed border-background/50 relative flex justify-center items-center">
                            {uploadLogoLoading ? (
                              <div className="flex items-center gap-2">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width={24}
                                  height={24}
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="animate-spin"
                                >
                                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                </svg>
                              </div>
                            ) : photoUrl ? (
                              <img
                                src={import.meta.env.VITE_MEDIA_BASE + photoUrl}
                                alt="photo de profil"
                                className="size-full object-cover"
                              />
                            ) : (
                              <div className="bg-none  p-0 rounded-full flex justify-center items-center flex-col">
                                <Camera size={24} className="mb-4" />
                                <p>
                                  {t("Glissez-déposez une capture d'écran")}
                                </p>
                              </div>
                            )}
                          </FileInput>
                        </FileUploader>
                      </div>
                    </div>
                    {enableExtension && (
                      <div className="bg-gray-100 w-full flex justify-between p-3">
                        <p className="m-0">
                          📸{" "}
                          {t(
                            "Installez notre extension navigateur pour que votre capture d'écran s'ajoute automatiquement à votre feedback."
                          )}{" "}
                          <br />
                          <em className="text-xs">
                            {" "}
                            (
                            {t(
                              "Sinon, vous pouvez toujours glisser-déposer vos captures manuellement."
                            )}
                            )
                          </em>
                        </p>
                        <Button>{t("Installer l'extension")}</Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="w-1/3  mt-auto h-full">
                <DialogHeader className="bg-red-600 py-4 text-center rounded-t-md">
                  <DialogTitle className="text-white font-normal flex justify-center gap-2 items-center">
                    <div className="bg-white size-5 flex justify-center items-center text-red-600 p-0.5 rounded-sm">
                      <PencilLine />
                    </div>
                    {t("Signaler un problème")}
                  </DialogTitle>
                </DialogHeader>
                <div className="p-4 bg-gray-100 space-y-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Titre")} *
                  </label>
                  <Input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Message")} *
                  </label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    placeholder={t(
                      "Merci de détailler le problème : message affiché, action effectuée et résultat obtenu."
                    )}
                    required
                  />
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Type")} *
                  </label>
                  <Select
                    value={type}
                    onValueChange={(value) => {
                      setType(value);
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t("Séléctionner un type")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>{t("Types")}</SelectLabel>
                        <SelectItem value="1">{t("Problème")}</SelectItem>
                        <SelectItem value="2">
                          {t("Problème d'interface (UI)")}
                        </SelectItem>
                        <SelectItem value="3">{t("Autres")}</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter className="py-4 bg-gray-200 rounded-b-md px-4 w-full">
                  <Button
                    type="submit"
                    className="w-full bg-red-600 text-white"
                  >
                    {t("Envoyer le rapport")}
                  </Button>
                </DialogFooter>
              </div>
            </form>
          ) : (
            <div className="bg-white w-full rounded-md relative p-4 min-h-96">
              <ScrollArea className="h-96 w-full ">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("Titre")}</TableHead>
                      <TableHead>{t("Message")}</TableHead>
                      <TableHead>{t("Type")}</TableHead>
                      <TableHead>{t("Statut")}</TableHead>
                      <TableHead>{t("Pièces jointes")}</TableHead>
                      <TableHead>{t("Date")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {feedbacks?.data?.map((feedback) => (
                      <TableRow key={feedback.id} className="align-top">
                        <TableCell className="font-medium w-52 align-top pt-7">
                          {feedback.title}
                        </TableCell>
                        <TableCell className="w-72 align-top">
                          <div className="border p-3 rounded-md border-[#f7f7f7] min-h-16">
                            {feedback.message}
                          </div>
                        </TableCell>
                        <TableCell className="w-44 align-top">
                          {feedback.type == 1
                            ? t("Problème")
                            : feedback.type == 2
                            ? t("Problème d'interface (UI)")
                            : t("Autres")}
                        </TableCell>

                        <TableCell className="align-top">
                          {feedback.status === 0 ? (
                            <Badge variant="default" className="bg-verdeoblue">
                              {t("En attente")}
                            </Badge>
                          ) : feedback.status === 1 ? (
                            <Badge
                              variant="default"
                              className="bg-verdeoorange"
                            >
                              {t("En cours")}
                            </Badge>
                          ) : (
                            <Badge variant="default" className="bg-verdeogreen">
                              {t("Résolu")}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="align-top">
                          {feedback.pjs?.map((pj) => (
                            <p key={pj.id}>
                              <a
                                href={import.meta.env.VITE_MEDIA_BASE + pj.uri}
                                className="flex gap-2"
                              >
                                <Download size={16} />
                                <span className="w-32 truncate">
                                  {pj.name.length > 30
                                    ? pj.name.substring(
                                        0,
                                        pj.name.lastIndexOf(".")
                                      )
                                    : pj.name}
                                </span>
                                .
                                {pj.name.length > 30 &&
                                  pj.name.substring(
                                    pj.name.lastIndexOf(".") + 1
                                  )}
                              </a>
                            </p>
                          ))}
                        </TableCell>
                        <TableCell className=" align-top">
                          {new Date(feedback.createdAt).toLocaleDateString()} à{" "}
                          {new Date(feedback.createdAt).toLocaleTimeString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}

          <button
            className={`absolute   bg-red-500 text-white z-50 py-1 px-3 rounded-sm ${
              extensionInstalled ? "top-4 right-4" : "top-0 right-6"
            }`}
            onClick={() => {
              setVisibleFeedback(false);
              clearErrorLog(); // AJOUT : Nettoyer le log d'erreur à la fermeture
            }}
          >
            {t("Fermer")}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
